/* eslint-disable spellcheck/spell-checker */
import { Injectable } from '@angular/core';

import { AppEventType, PlayerCountChangeEvent } from '../models/communication/app-events.model';
import { AppState } from '../models/communication/app-state.model';
import { YEventPayload } from '../models/y-protocol.model';

export type Plugin = (state: AppState, event: YEventPayload<AppEventType>, containerNode: HTMLElement) => void;

/**
 * Show the animation effect by adding the class and remove it after the animation ends.
 * While it runs, the one listener already waiting for its end is enough: a listener per call piles up whenever the animation
 * never ends (nothing in the page matches the class, as with `.player-count` since the header redesign).
 */
const initAnimation = (node: HTMLElement, className: string): void => {
    if (node.classList.contains(className)) {
        return;
    }

    node.classList.add(className);
    node.addEventListener('animationend', () => node.classList.remove(className), { once: true });
};

/**
 * PluginService exposes the array of plugin functions that get called
 * every time the state or event stream emit values.
 *
 * It is intended for a specific business logic and requirements
 * that would otherwise pollute the app.component with unnecessary details.
 *
 * For now, the function signature is based on the current plugin requirements.
 * In the future, a more general interface should be defined.
 *
 */
@Injectable({
    providedIn: 'root'
})
export class PluginService {
    readonly plugins: Plugin[] = [
        /**
         * Highlight the player count when the number of players increases.
         */
        (state: AppState, { name, data }: YEventPayload<AppEventType>, containerNode: HTMLElement): void => {
            if (name === AppEventType.PlayerCountChange && (data as PlayerCountChangeEvent).count > (state.playerCount || 0)) {
                initAnimation(containerNode, 'highlight-player-count');
            }
        }
    ];
}
